/**
 * US states — `GET /v2/states/`.
 *
 * Backs the Facility Schedule "State" picker and the Loss Runs "Venue"
 * picker (both atlas SearchSelects). The stored value is the 2-letter
 * code; the code is shown as the label.
 */
import { getJsonReal } from "../client";

export interface UsState {
    code: string;
    name: string;
    /** State premium tax rate as a 0–1 fraction (e.g. 0.06), or null. */
    taxRate: number | null;
}

interface UsStateWire {
    code: string;
    name: string;
    tax_rate: number | null;
}

export function getStates(): Promise<UsState[]> {
    return getJsonReal<UsStateWire[]>("/v2/states/").then((rows) =>
        rows.map((r) => ({ code: r.code, name: r.name, taxRate: r.tax_rate ?? null })),
    );
}

/**
 * Single state — `GET /v2/states/{code}`.
 *
 * Adds `hasTaxableFees`: whether this state's Surplus Lines Tax/Fee taxable
 * base includes fees (e.g. Tech/RM Fee) alongside Premium, or Premium alone
 * (OR-1440). Used by Premium Selection right after a Calculate Tax to look
 * the per-state rule up directly instead of reverse-solving it from
 * InsCipher's filed amount/rate.
 */
export interface UsStateDetail extends UsState {
    hasTaxableFees: boolean;
}

interface UsStateDetailWire extends UsStateWire {
    has_taxable_fees: boolean;
}

export function getState(code: string): Promise<UsStateDetail> {
    return getJsonReal<UsStateDetailWire>(`/v2/states/${encodeURIComponent(code)}`).then((r) => ({
        code: r.code,
        name: r.name,
        taxRate: r.tax_rate ?? null,
        hasTaxableFees: r.has_taxable_fees,
    }));
}
